import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Loader2, ArrowLeft, ArrowRight, Printer, Copy, Mail, X, Wrench, AlertTriangle, Package } from 'lucide-react';
import api from '../services/api';

const STEPS = {
  UPLOAD: 0,
  ANALYZING: 1,
  CATEGORIES: 2,
  PLANNING: 3,
  REVIEW: 4,
  QUOTE: 5,
};

const PhotoJobWizard = ({ open, onClose, onTaskCreated }) => {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherDescription, setOtherDescription] = useState('');
  const [plan, setPlan] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setPreview(null);
    setCategories([]);
    setSelectedCategory(null);
    setShowOtherInput(false);
    setOtherDescription('');
    setPlan(null);
    setQuote(null);
    setQuoteLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setStep(STEPS.ANALYZING);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/ai/analyze-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const raw = res.data.categories;
      let parsed = typeof raw === 'string' ? JSON.parse(raw.replace(/```json\n?|```/g, '').trim()) : raw;
      setCategories(parsed);
      setStep(STEPS.CATEGORIES);
    } catch (error) {
      toast.error('Failed to analyze image. Please try again.');
      setStep(STEPS.UPLOAD);
    }
  };

  const handleSelectCategory = async (cat) => {
    setSelectedCategory(cat);
    setStep(STEPS.PLANNING);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/ai/plan-from-image?category=${encodeURIComponent(cat.title)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const raw = res.data.plan;
      let parsed = typeof raw === 'string' ? JSON.parse(raw.replace(/```json\n?|```/g, '').trim()) : raw;
      setPlan(parsed);
      setStep(STEPS.REVIEW);
    } catch (error) {
      toast.error('Failed to generate plan. Please try again.');
      setStep(STEPS.CATEGORIES);
    }
  };

  const handleGenerateQuote = async () => {
    if (!plan) return;
    setQuoteLoading(true);
    try {
      const res = await api.post('/ai/generate-quote', {
        title: plan.title,
        description: plan.description,
        parts: plan.parts,
        tools: plan.tools,
        estimate_hours: plan.estimate_hours
      });
      const raw = res.data.quote;
      let parsed = typeof raw === 'string' ? JSON.parse(raw.replace(/```json\n?|```/g, '').trim()) : raw;
      setQuote(parsed);
      setStep(STEPS.QUOTE);
    } catch (error) {
      toast.error('Failed to generate quote.');
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!plan) return;
    try {
      const taskRes = await api.post('/tasks', {
        title: plan.title,
        description: plan.description,
        estimate: plan.estimate_hours,
        status: 'OPEN',
      });
      const taskId = taskRes.data.task_id;

      // Add parts
      for (const part of plan.parts || []) {
        await api.post(`/tasks/${taskId}/parts`, {
          name: part.name,
          quantity: part.quantity || 1,
          supplier: part.supplier || null,
          unit_price: part.unit_price || null
        });
      }

      // Add tools
      for (const tool of plan.tools || []) {
        await api.post(`/tasks/${taskId}/tools`, { name: tool });
      }

      // Attach the original photo to the task
      if (file) {
        const photoFormData = new FormData();
        photoFormData.append('file', file);
        await api.post(`/tasks/${taskId}/attachments`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Task created from photo!');
      handleClose();
      if (onTaskCreated) onTaskCreated(taskId);
    } catch (error) {
      toast.error('Failed to create task.');
    }
  };

  const copyQuote = () => {
    if (!quote) return;
    const text = `${plan.title}\n\n${quote.summary}\n\nMaterials: ${quote.materials_estimate}\nLabour (${quote.labour_hours}hrs): ${quote.labour_estimate}\nTotal Estimate: ${quote.total_estimate}\n\n${quote.notes || ''}`;
    navigator.clipboard.writeText(text);
    toast.success('Quote copied to clipboard');
  };

  const shareQuoteEmail = () => {
    if (!quote) return;
    const subject = encodeURIComponent(`Quote: ${plan.title}`);
    const body = encodeURIComponent(`${plan.title}\n\n${quote.summary}\n\nMaterials: ${quote.materials_estimate}\nLabour (${quote.labour_hours}hrs): ${quote.labour_estimate}\nTotal Estimate: ${quote.total_estimate}\n\n${quote.notes || ''}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Camera className="h-5 w-5 text-amber-500" />
            {step === STEPS.UPLOAD && 'New Job from Photo'}
            {step === STEPS.ANALYZING && 'Analysing job...'}
            {step === STEPS.CATEGORIES && 'What type of job is this?'}
            {step === STEPS.PLANNING && 'Building your parts list...'}
            {step === STEPS.REVIEW && 'Review Job Plan'}
            {step === STEPS.QUOTE && 'Job Quote'}
          </DialogTitle>
          <DialogDescription className="sr-only">Photo to job plan wizard</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
          {/* Step 1: Upload */}
          {step === STEPS.UPLOAD && (
            <div className="space-y-4 py-4">
              <p className="text-slate-600">
                Take a photo of the job or problem and AI will create a task with a parts list, tools, and safety cautions.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="Job photo" className="w-full max-h-64 object-contain rounded-lg border" />
                  <Button
                    variant="ghost" size="sm"
                    className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                    onClick={() => { setFile(null); setPreview(null); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-amber-400 hover:bg-amber-50/50 transition-colors cursor-pointer"
                  data-testid="photo-upload-area"
                >
                  <Camera className="h-10 w-10 text-slate-400" />
                  <span className="text-slate-600 font-medium">Upload or capture a photo</span>
                  <span className="text-sm text-slate-400">JPEG, PNG, or WEBP</span>
                </button>
              )}
            </div>
          )}

          {/* Step 2: Analyzing spinner */}
          {step === STEPS.ANALYZING && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
              <p className="text-slate-600 font-medium">Analysing your photo...</p>
              <p className="text-sm text-slate-400">This usually takes a few seconds</p>
            </div>
          )}

          {/* Step 3: Category selection */}
          {step === STEPS.CATEGORIES && (
            <div className="space-y-3 py-4">
              {preview && (
                <img src={preview} alt="Job photo" className="w-full max-h-32 object-contain rounded-lg border mb-4" />
              )}
              <p className="text-slate-600 mb-2">Select the category that best matches your job:</p>
              {categories.map((cat, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectCategory(cat)}
                  className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all group"
                  data-testid={`category-card-${idx}`}
                >
                  <p className="font-medium text-slate-900 group-hover:text-amber-700">{cat.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{cat.description}</p>
                </button>
              ))}
              {/* Other option */}
              {!showOtherInput ? (
                <button
                  onClick={() => setShowOtherInput(true)}
                  className="w-full text-left p-4 rounded-lg border border-dashed border-slate-300 hover:border-amber-400 hover:bg-amber-50/50 transition-all group"
                  data-testid="category-card-other"
                >
                  <p className="font-medium text-slate-900 group-hover:text-amber-700">Other</p>
                  <p className="text-sm text-slate-500 mt-1">Describe the job yourself</p>
                </button>
              ) : (
                <div className="p-4 rounded-lg border border-amber-300 bg-amber-50/50 space-y-3">
                  <p className="font-medium text-slate-900">Describe the job in the photo:</p>
                  <Textarea
                    value={otherDescription}
                    onChange={(e) => setOtherDescription(e.target.value)}
                    placeholder="e.g., Leaking tap in kitchen, needs washer replacement"
                    rows={2}
                    className="bg-white"
                    data-testid="other-description-input"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!otherDescription.trim()) { toast.error('Please describe the job'); return; }
                        handleSelectCategory({ title: otherDescription.trim(), description: 'User-provided description' });
                      }}
                      disabled={!otherDescription.trim()}
                      data-testid="other-submit-btn"
                    >
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShowOtherInput(false); setOtherDescription(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={() => setStep(STEPS.UPLOAD)} className="mt-2">
                <ArrowLeft className="h-4 w-4 mr-2" />Try a different photo
              </Button>
            </div>
          )}

          {/* Step 4: Planning spinner */}
          {step === STEPS.PLANNING && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
              <p className="text-slate-600 font-medium">Building your parts list...</p>
              <p className="text-sm text-slate-400">Generating task plan, parts, and tools</p>
            </div>
          )}

          {/* Step 5: Review plan */}
          {step === STEPS.REVIEW && plan && (
            <div className="space-y-5 py-4">
              {/* Title & Description */}
              <div>
                <Label>Task Title</Label>
                <Input
                  value={plan.title}
                  onChange={(e) => setPlan({ ...plan, title: e.target.value })}
                  className="mt-1"
                  data-testid="plan-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={plan.description}
                  onChange={(e) => setPlan({ ...plan, description: e.target.value })}
                  rows={3}
                  className="mt-1"
                  data-testid="plan-description"
                />
              </div>
              <div>
                <Label>Estimated Hours</Label>
                <Input
                  type="number"
                  value={plan.estimate_hours || ''}
                  onChange={(e) => setPlan({ ...plan, estimate_hours: parseFloat(e.target.value) || 0 })}
                  className="mt-1 w-32"
                />
              </div>

              {/* Parts */}
              <div>
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-500" />
                  Parts ({plan.parts?.length || 0})
                </Label>
                <div className="mt-2 space-y-2">
                  {plan.parts?.map((part, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="flex-1 font-medium">{part.name}</span>
                      <span className="text-slate-500">x{part.quantity}</span>
                      {part.unit_price != null && <span className="text-green-600 text-xs font-medium">${Number(part.unit_price).toFixed(2)}</span>}
                      {part.supplier && <Badge variant="outline" className="text-xs">{part.supplier}</Badge>}
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                        onClick={() => setPlan({ ...plan, parts: plan.parts.filter((_, i) => i !== idx) })}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tools */}
              <div>
                <Label className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-blue-500" />
                  Tools ({plan.tools?.length || 0})
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {plan.tools?.map((tool, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {tool}
                      <button onClick={() => setPlan({ ...plan, tools: plan.tools.filter((_, i) => i !== idx) })}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Cautions */}
              {plan.cautions?.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Safety Cautions
                  </Label>
                  <div className="mt-2 space-y-1">
                    {plan.cautions.map((c, idx) => (
                      <p key={idx} className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded">{c}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Quote */}
          {step === STEPS.QUOTE && quote && (
            <div className="space-y-5 py-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(STEPS.REVIEW)}>
                <ArrowLeft className="h-4 w-4 mr-2" />Back to plan
              </Button>
              <Card>
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-heading font-bold text-lg">{plan.title}</h3>
                  <p className="text-slate-700 leading-relaxed">{quote.summary}</p>
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Materials</span>
                      <span className="font-medium">{quote.materials_estimate}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Labour ({quote.labour_hours} hrs)</span>
                      <span className="font-medium">{quote.labour_estimate}</span>
                    </div>
                    <div className="flex justify-between text-base border-t pt-2 mt-2">
                      <span className="font-semibold">Total Estimate</span>
                      <span className="font-bold text-amber-600">{quote.total_estimate}</span>
                    </div>
                  </div>
                  {quote.notes && (
                    <p className="text-xs text-slate-500 italic">{quote.notes}</p>
                  )}
                </CardContent>
              </Card>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyQuote}>
                  <Copy className="h-4 w-4 mr-2" />Copy
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="h-4 w-4 mr-2" />Print
                </Button>
                <Button variant="outline" size="sm" onClick={shareQuoteEmail}>
                  <Mail className="h-4 w-4 mr-2" />Email
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          {step === STEPS.UPLOAD && (
            <div className="flex w-full justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleAnalyze} disabled={!file} data-testid="analyze-photo-btn">
                Analyse Photo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
          {step === STEPS.REVIEW && (
            <div className="flex w-full justify-between">
              <Button variant="ghost" onClick={() => setStep(STEPS.CATEGORIES)}>
                <ArrowLeft className="mr-2 h-4 w-4" />Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleGenerateQuote} disabled={quoteLoading}>
                  {quoteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Request a Quote
                </Button>
                <Button onClick={handleCreateTask} data-testid="create-task-from-photo-btn">
                  Create Task
                </Button>
              </div>
            </div>
          )}
          {step === STEPS.QUOTE && (
            <div className="flex w-full justify-between">
              <Button variant="ghost" onClick={() => setStep(STEPS.REVIEW)}>
                <ArrowLeft className="mr-2 h-4 w-4" />Back to plan
              </Button>
              <Button onClick={handleCreateTask} data-testid="create-task-from-quote-btn">
                Create Task
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoJobWizard;
